import { useQuery } from '@tanstack/react-query';
import {
  ChatCloseIcon,
  ChatLocationIcon,
  Disclaimer,
  getAllVerifiedLocations,
  lightTheme,
  P,
  queryKeys,
  SearchBar,
  SearchField,
  SimpleLocationEntity,
  TruncatedText,
  useDebouncedValue,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  ChipName,
  ClearButton,
  FilterAnchor,
  LocationChip,
  Suggestion,
  Suggestions,
} from './CandidateLocationFilter.styled';

const SEARCH_DEBOUNCE_MS = 300;
/** A short pick-list, not a browser: the name narrows it. */
const SUGGESTION_LIMIT = 8;

type Props = {
  location: SimpleLocationEntity | null;
  onChange: (location: SimpleLocationEntity | null) => void;
};

/**
 * Narrows the merchant group member picker to the people of one location.
 *
 * Who belongs in a group is usually "everyone at Burgerito", and a name search
 * cannot say that: owners, managers and staff are named after themselves, not
 * after the restaurant. The endpoint takes a `locationId` for exactly this.
 *
 * A field until something is picked, then a pill saying what the list is
 * narrowed to. The suggestions only exist while the field has focus, so a
 * stray result list never sits over the people underneath.
 */
export const CandidateLocationFilter = ({ location, onChange }: Props) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const debounced = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.conversations.candidateLocations(debounced || null),
    queryFn: async () => {
      const { data: response } = await getAllVerifiedLocations({
        ...(debounced ? { query: debounced } : {}),
        page: 1,
        limit: SUGGESTION_LIMIT,
      });
      return response.data;
    },
    enabled: focused && !location,
    staleTime: 60 * 1000,
  });

  if (location) {
    return (
      <FilterAnchor>
        <LocationChip>
          <ChatLocationIcon color={lightTheme.primary} />
          <ChipName>
            <P $m="0" $bold>
              <TruncatedText>{location.name}</TruncatedText>
            </P>
          </ChipName>
          <ClearButton
            type="button"
            aria-label="Clear location"
            onClick={() => onChange(null)}
          >
            <ChatCloseIcon color={lightTheme.darkGray} />
          </ClearButton>
        </LocationChip>
      </FilterAnchor>
    );
  }

  return (
    <FilterAnchor>
      <SearchBar>
        <SearchField
          value={query}
          placeholder="Filter by location"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(event.target.value)
          }
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <ChatLocationIcon />
      </SearchBar>

      {focused && (
        <Suggestions>
          {(data ?? []).map((item: SimpleLocationEntity) => (
            <Suggestion
              key={item.id}
              type="button"
              /* On mouse down, not click: the field's blur fires between the
                 two and would take the list away before the click lands. */
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(item);
                setQuery('');
                setFocused(false);
              }}
            >
              <P $m="0" $bold>
                <TruncatedText>{item.name}</TruncatedText>
              </P>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                <TruncatedText color={lightTheme.lightGray}>
                  {item.address}
                </TruncatedText>
              </Disclaimer>
            </Suggestion>
          ))}

          {!isFetching && data?.length === 0 && (
            <Disclaimer $m="8px 10px" $color={lightTheme.lightGray}>
              No location matches that search.
            </Disclaimer>
          )}
          {isFetching && !data && (
            <Disclaimer $m="8px 10px" $color={lightTheme.lightGray}>
              Searching...
            </Disclaimer>
          )}
        </Suggestions>
      )}
    </FilterAnchor>
  );
};
